import { createRef, forwardRef, HTMLAttributes, memo } from "react";
import { twMerge } from "tailwind-merge";
import { useAttendeeStatus } from "amazon-chime-sdk-component-library-react";
import { useActiveSpeakers } from "@platform/features/video/hooks/useActiveSpeakers";
import type { ConnectionQuality } from "@core/features/video";
import type { RoomMember } from "@core/features/videoMeeting";
import { useVideoReactions } from "@platform/store";
import VideoMeetingTileNameplate from "./VideoMeetingTileNameplate";
import VideoMeetingTileNameplateType from "./VideoMeetingTileNameplateType";
import VideoMeetingTileAudioIcon from "./VideoMeetingTileAudioIcon";
import VideoMeetingTileBandwidthPill from "./VideoMeetingTileBandwidthPill";
import VideoMeetingTileConnectionIcon from "./VideoMeetingTileConnectionIcon";
import { VideoMeetingTileReactionOverlay } from "./VideoMeetingTileReactionOverlay";
import useContainerWidthBreakpoint from "@core/hooks/useContainerWidthBreakpoint";

type ObjectFit = "contain" | "cover" | "fill" | "none" | "scale-down";

export interface VideoMeetingTileProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "css"
> {
  id?: string;
  isEnabled?: boolean;
  isContentShare?: boolean;
  isWithContentShare?: boolean;
  tileIndex?: number;
  totalTiles?: number;
  nameplate?: string;
  objectFit?: ObjectFit;
  attendeeId?: string;
  roomMemberData?: RoomMember | null;
  videoClassName?: string;
  connectionQuality?: ConnectionQuality;
  canvasRef?: React.Ref<HTMLCanvasElement>;
  showFrozenFrame?: boolean;
  /** Render a "Limited video reception" pill on the avatar layer when the
   *  SDK has paused this attendee's tile under bandwidth pressure (tile
   *  state === 'bandwidth_suppressed'). Local self-view bypasses
   *  attendeeTileState entirely; isBandwidthSuppressed is always
   *  undefined for self-view. If a future refactor unifies local + remote
   *  tile rendering, gate this on `attendeeId !== localAttendeeId`. */
  isBandwidthSuppressed?: boolean;
}

export const VideoMeetingTile = memo(
  forwardRef<HTMLVideoElement, VideoMeetingTileProps>(
    (
      {
        className,
        nameplate,
        isEnabled,
        isContentShare,
        isWithContentShare,
        objectFit = "cover",
        attendeeId,
        roomMemberData,
        videoClassName,
        connectionQuality,
        canvasRef,
        showFrozenFrame,
        isBandwidthSuppressed,
        tileIndex: _tileIndex,
        totalTiles: _totalTiles,
        ...rest
      },
      ref,
    ) => {
      const containerRef = createRef<HTMLDivElement>();

      const { isXs, containerWidth } =
        useContainerWidthBreakpoint(containerRef);

      const { muted } = useAttendeeStatus(attendeeId || "");

      const { isActiveSpeaker } = useActiveSpeakers();

      const isSpeaking = attendeeId ? isActiveSpeaker(attendeeId) : false;

      const { attendeeReactions } = useVideoReactions();
      const reactions = attendeeId
        ? attendeeReactions[attendeeId] ?? []
        : [];

      const showVideo = isEnabled || isContentShare;

      return (
        <div
          className={twMerge(
            "relative rounded-md bg-brand-800 w-full h-full group transition-all duration-200",
            isSpeaking && "ring-1 ring-brand-100 ring-offset-1",
            className,
          )}
          data-testid="video-tile"
          ref={containerRef}
          {...rest}
        >
          {/* Video wrapper — hides the video element when not needed.
              Styles are on the wrapper because the Chime SDK sets inline
              styles on the <video> element that override Tailwind classes. */}
          <div className={twMerge("absolute inset-0", !showVideo && "hidden")}>
            <video
              ref={ref}
              className={twMerge(
                "absolute inset-0 w-full h-full rounded-md",
                objectFit === "cover" && "object-cover",
                objectFit === "contain" && "object-contain",
                objectFit === "fill" && "object-fill",
                objectFit === "none" && "object-none",
                objectFit === "scale-down" && "object-scale-down",
                videoClassName,
              )}
            />
          </div>

          {canvasRef && (
            <div
              className={twMerge(
                "absolute inset-0",
                !showFrozenFrame && "hidden",
              )}
            >
              <canvas
                ref={canvasRef}
                data-testid="frozen-frame-canvas"
                className="absolute inset-0 w-full h-full rounded-md pointer-events-none"
              />
            </div>
          )}

          {connectionQuality && (
            <VideoMeetingTileConnectionIcon
              connectionQuality={connectionQuality}
              isWithContentShare={isWithContentShare}
            />
          )}

          {attendeeId && (
            <VideoMeetingTileReactionOverlay
              reactions={reactions}
              senderName={nameplate}
            />
          )}

          {showVideo ? (
            <>
              {nameplate && (
                <VideoMeetingTileNameplate
                  nameplate={nameplate}
                  isWithContentShare={!!isWithContentShare}
                  isWithVideo={!!isEnabled}
                  roomMemberData={roomMemberData}
                  containerWidth={containerWidth}
                  isXs={isXs}
                />
              )}
              <VideoMeetingTileAudioIcon
                muted={muted}
                isWithContentShare={isWithContentShare}
              />
            </>
          ) : (
            <div
              className={twMerge(
                "absolute inset-0 z-10 flex items-center justify-center bg-brand-800 rounded-md",
              )}
            >
              <VideoMeetingTileNameplateType
                nameplate={nameplate || ""}
                isWithContentShare={!!isWithContentShare}
                isWithVideo={false}
                roomMemberData={roomMemberData}
                containerWidth={containerWidth}
                isXs={isXs}
              />
              <VideoMeetingTileAudioIcon
                muted={muted}
                isWithContentShare={isWithContentShare}
              />
              {isBandwidthSuppressed && <VideoMeetingTileBandwidthPill />}
            </div>
          )}
        </div>
      );
    },
  ),
);
